import PageMeta from "../components/PageMeta";
import PublisherCard from "../components/PublisherCard";
import { isResidentialBien, isLandBien, bienCategoryLabel } from "../Services/bienEditModel";
import uiI18n from "../i18n";
import { uniquePlaceParts } from "../utils/placeLabels";
import { useTranslation as useUiTranslation } from "react-i18next";
import { shareListing } from "../Services/shareService";
import EngagementActions from "../components/EngagementActions";
import PrintButton from "../components/PrintButton";
import { sendMessage } from "../Services/messageService";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useParams } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useTranslation } from "react-i18next";

import {
  Swiper,
  SwiperSlide,
} from "swiper/react";

import {
  Navigation,
  Pagination,
} from "swiper/modules";

import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
} from "react-leaflet";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

import {
  LuBath,
  LuBedDouble,
  LuCar,
  LuCheck,
  LuExternalLink,
  LuImages,
  LuMapPin,
  LuMaximize2,
  LuMessageSquare,
  LuPhone,
  LuSend,
  LuShare2,
  LuLoaderCircle,
  LuSofa,
  LuTag,
  LuX,
} from "react-icons/lu";

import Spinner from "../components/Spinner";
import { variables, photoUrl } from "../Variables";
import { getBienById } from "../Services/biensService";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Leaflet's default icon guesses its image path from the CSS, which fails once bundled (broken images):
// the marker of the detail map uses the bundled Leaflet images explicitly.
const listingMarkerIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const getDefaultMessage = () => uiI18n.t("ui.property_message");

const FALLBACK_IMAGE =
  "/images/property-placeholder.jpg";

const inputClassName = `
  h-12
  w-full
  rounded-2xl
  border
  border-slate-200
  bg-white
  px-4
  text-sm
  font-medium
  text-slate-900
  outline-none
  transition
  placeholder:text-slate-400
  hover:border-slate-300
  focus:border-green-500
  focus:ring-4
  focus:ring-green-500/10
`;

function Listing() {
  const { t } = useTranslation();
  const { bienID } = useParams();
useEffect(() => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "auto",
  });
}, [bienID]);

  const auth = getAuth();

  /* =========================
      ÉTATS
  ========================== */

  const [bien, setBien] = useState(null);

  const [loading, setLoading] =
    useState(true);

  const [
    loadingError,
    setLoadingError,
  ] = useState("");

  const [
    contactError,
    setContactError,
  ] = useState("");

  const [
    contactLandlord,
    setContactLandlord,
  ] = useState(false);

  const [sending, setSending] =
    useState(false);

  // Synchronous guard: a double click fires twice before "sending" re-renders.
  const sendingRef = useRef(false);

  const [
    sendSuccess,
    setSendSuccess,
  ] = useState(false);

  const [
    shareLinkCopied,
    setShareLinkCopied,
  ] = useState(false);

  const [
    galleryOpen,
    setGalleryOpen,
  ] = useState(false);

  const [
    activeImageIndex,
    setActiveImageIndex,
  ] = useState(0);

  const [formData, setFormData] =
    useState({
      contactName: "",
      email: "",
      phone: "",
      message: getDefaultMessage(),
    });

  /* =========================
      CHARGEMENT ANNONCE
  ========================== */

  useEffect(() => {
    let componentMounted = true;

    const loadListing = async () => {
      setLoading(true);
      setLoadingError("");

      try {
        const response =
          await getBienById(bienID);

        if (componentMounted) {
          setBien(response || null);
        }
      } catch (requestError) {
        if ([404, 410].includes(requestError?.status)) {
          // Unknown or deleted listing: the « introuvable » page (the server answers 404 to crawlers).
          if (componentMounted) setBien(null);
          return;
        }
        console.error(
          "Erreur chargement annonce :",
          requestError
        );

        if (componentMounted) {
          setLoadingError(
            requestError?.message ||
              t(
                "listing.loadingError",
                "Impossible de charger cette annonce."
              )
          );
        }
      } finally {
        if (componentMounted) {
          setLoading(false);
        }
      }
    };

    loadListing();

    return () => {
      componentMounted = false;
    };
  }, [bienID, t]);

  /* =========================
      PRÉREMPLIR UTILISATEUR
  ========================== */

  useEffect(() => {
    const user = auth.currentUser;

    if (!user) {
      return;
    }

    setFormData((previousValue) => ({
      ...previousValue,

      contactName:
        previousValue.contactName ||
        user.displayName ||
        "",

      email:
        previousValue.email ||
        user.email ||
        "",
    }));
  }, [auth]);

  /* =========================
      IMAGES
  ========================== */

  const images = useMemo(() => {
    if (!bien?.urlImages) {
      return [];
    }

    const rawImages = Array.isArray(
      bien.urlImages
    )
      ? bien.urlImages
      : String(bien.urlImages).split(",");

    return rawImages
      .map((image) =>
        String(image).trim()
      )
      .filter(Boolean);
  }, [bien?.urlImages]);

  /* =========================
      PRIX
  ========================== */

  const priceInformation =
    useMemo(() => {
      const normalPrice =
        Number(bien?.prix) || 0;

      const reducedPrice =
        Number(bien?.prixReduit) || 0;

      const hasOffer =
        Boolean(bien?.offre) &&
        normalPrice > 0 &&
        reducedPrice > 0 &&
        reducedPrice < normalPrice;

      const finalPrice = hasOffer
        ? reducedPrice
        : normalPrice;

      const savedAmount = hasOffer
        ? normalPrice - reducedPrice
        : 0;

      const discountPercentage =
        hasOffer && normalPrice > 0
          ? Math.round(
              ((normalPrice -
                reducedPrice) /
                normalPrice) *
                100
            )
          : 0;

      return {
        normalPrice,
        reducedPrice,
        finalPrice,
        savedAmount,
        discountPercentage,
        hasOffer,
      };
    }, [bien]);

  const formatPrice = (value) =>
    new Intl.NumberFormat(uiI18n.language, {
      maximumFractionDigits: 0,
    }).format(Number(value) || 0);

  /* =========================
      LOCALISATION
  ========================== */

  const locationText = useMemo(() => {
    if (!bien) {
      return "";
    }

    return uniquePlaceParts([
      [bien.adresse, bien.adresse],
      [bien.delegation, t(`delegations.${bien.ville}.${bien.delegation}`, bien.delegation || '')],
      [bien.ville, t(`cities.${bien.ville}`, bien.ville || '')],
    ]).join(", ");
  }, [bien, t]);

  const latitude = Number(
    bien?.latitude
  );

  const longitude = Number(
    bien?.longitude
  );

  const hasValidCoordinates =
    Number.isFinite(latitude) &&
    Number.isFinite(longitude);

  /* =========================
      PROPRIÉTAIRE
  ========================== */

  const isOwner =
    Boolean(auth.currentUser?.uid) &&
    auth.currentUser.uid === bien?.uid;

  const isLand = [bien?.category, bien?.categorie, bien?.typeBien].some(isLandBien);

  /* =========================
      PARTAGE
  ========================== */

  const [sharing, setSharing] = useState(false);
  const shareLock = useRef(false);
  const handleShare = async () => {
    if (shareLock.current) return;
    shareLock.current = true; setSharing(true);
    try { const message = await shareListing("bien", bien); setShareLinkCopied(Boolean(message)); }
    catch (error) { if(error.name !== "AbortError") setContactError(uiI18n.t("ui.share_failed")); }
    finally { shareLock.current = false; setSharing(false); }
  };

  /* =========================
      FORMULAIRE CONTACT
  ========================== */

  const handleFormChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target;

    let normalizedValue = value;

    if (name === "phone") {
      normalizedValue = value
        .replace(/[^0-9+\s]/g, "")
        .slice(0, 20);
    }

    setFormData(
      (previousValue) => ({
        ...previousValue,
        [name]: normalizedValue,
      })
    );

    setSendSuccess(false);
    setContactError("");
  };

  const handleFormSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (sending || sendingRef.current || !bien) {
      return;
    }

    const contactName =
      formData.contactName.trim();

    const email =
      formData.email.trim();

    const phone =
      formData.phone.trim();

    const message =
      formData.message.trim();

    if (
      !contactName ||
      !email ||
      !phone ||
      !message
    ) {
      setContactError(
        t(
          "listing.completeFields",
          "Veuillez compléter tous les champs."
        )
      );

      return;
    }

    sendingRef.current = true;
    setSending(true);
    setSendSuccess(false);
    setContactError("");

    try {
      await sendMessage({ receiverId: bien.uid, propertyId: bien.id || bien.ID,
        content: message, isVacation: false, propertyEmail: bien.email,
        name: bien.name, adLabel: bien.libelle, adsUrl: window.location.href });

      // Sent once: the text is cleared and the form closed, the confirmation (outside the form) stays visible.
      setFormData((previousValue) => ({ ...previousValue, message: "" }));
      setContactLandlord(false);
      setSendSuccess(true);
    } catch (requestError) {
      console.error(
        "Erreur envoi message :",
        requestError
      );

      setContactError(
        requestError?.message ||
          t(
            "listing.networkError",
            "Une erreur réseau est survenue."
          )
      );
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  /* =========================
      CHARGEMENT
  ========================== */

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-slate-50">
        <Spinner />
      </div>
    );
  }

  /* =========================
      ERREUR
  ========================== */

  if (loadingError && !bien) {
    return (
      <main className="min-h-[60vh] bg-slate-50 px-4 py-16">
        <div className="mx-auto max-w-2xl rounded-3xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <h1 className="text-xl font-black text-red-800">
            {t(
              "listing.loadingTitle",
              "Impossible de charger l’annonce"
            )}
          </h1>

          <p className="mt-3 text-sm leading-6 text-red-600">
            {loadingError}
          </p>
        </div>
      </main>
    );
  }

  /* =========================
      INTROUVABLE
  ========================== */

  if (!bien) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center bg-slate-50 px-4">
        <PageMeta titleKey="seo.notFound" />
        <div className="text-center">
          <LuImages
            size={42}
            className="mx-auto text-slate-300"
          />

          <h1 className="mt-4 text-xl font-black text-slate-900">
            {t(
              "listing.noData",
              "Cette annonce est introuvable."
            )}
          </h1>
        </div>
      </main>
    );
  }

  /* =========================
      PAGE
  ========================== */

  return (
    <main className="min-h-screen bg-slate-50">
      <PageMeta titleKey="seo.listing" title={bien.libelle} />
      {/* Galerie */}

      <ListingGallery
        actions={<>
          <PrintButton type="property" id={bien.id} iconOnly />
          <button type="button" className="listing-photo-action" onClick={handleShare}
            disabled={sharing} aria-busy={sharing}
            aria-label={uiI18n.t("ui.share_listing")} title={uiI18n.t("ui.share_listing")}>
            {sharing ? <LuLoaderCircle size={18} className="animate-spin" /> : <LuShare2 size={18} />}
          </button>
          <EngagementActions kind="bien" listing={bien} showShare={false} iconOnly />
        </>}
        images={images}
        photoPath={
          variables.PHOTO_URL
        }
        folder={bien.folder || ""}
        title={
          bien.libelle ||
          uiI18n.t("ui.property_listing")
        }
        galleryOpen={galleryOpen}
        setGalleryOpen={
          setGalleryOpen
        }
        activeImageIndex={
          activeImageIndex
        }
        setActiveImageIndex={
          setActiveImageIndex
        }
        shareLinkCopied={
          shareLinkCopied
        }
        copiedLabel={t(
          "listing.shareLinkCopied",
          "Lien copié"
        )}
      />

      {/* Contenu */}

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <div className="lina-detail-layout grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,380px)]">

          {/* =====================
              COLONNE PRINCIPALE
          ====================== */}

          <div className="space-y-6">

            {/* Informations principales */}

            <article className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:p-7">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">

                {/* Titre */}

                <div className="min-w-0">
                  <div className="mb-3 flex flex-wrap items-center gap-2">

                    <span
                      className={`
                        inline-flex
                        items-center
                        gap-1.5
                        rounded-full
                        px-3
                        py-1.5
                        text-xs
                        font-black
                        uppercase
                        tracking-wide
                        ${
                          bien.type ===
                          "rent"
                            ? "bg-blue-50 text-blue-700"
                            : "bg-emerald-50 text-emerald-700"
                        }
                      `}
                    >
                      <LuTag size={13} />

                      {bien.type === "rent"
                        ? t(
                            "listing.rent",
                            "À louer"
                          )
                        : t(
                            "listing.sale",
                            "À vendre"
                          )}
                    </span>

                    {priceInformation.hasOffer && (
                      <span className="rounded-full bg-red-50 px-3 py-1.5 text-xs font-black text-red-600">
                        {t(
                          "offer",
                          "Offre spéciale"
                        )}
                      </span>
                    )}

                    {bien.category && (
                      <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600">
                        {bienCategoryLabel(t, bien.category)}
                      </span>
                    )}
                  </div>

                  <h1 className="text-2xl font-black leading-tight text-slate-950 sm:text-3xl lg:text-4xl">
                    {bien.libelle ||
                      t(
                        "listing.property",
                        "Annonce immobilière"
                      )}
                  </h1>

                  {locationText && (
                    <p className="mt-4 flex items-start gap-2 text-sm text-slate-500 sm:text-base">
                      <LuMapPin
                        size={19}
                        className="mt-0.5 shrink-0 text-green-600"
                      />

                      <span>
                        {locationText}
                      </span>
                    </p>
                  )}
                </div>

                {/* Prix */}

                {priceInformation.finalPrice > 0 && <div className="shrink-0 sm:text-end">
                  {priceInformation.hasOffer && (
                    <div className="flex items-center gap-2 sm:justify-end">

                      <p className="text-sm font-semibold text-slate-400 line-through">
                        {formatPrice(
                          priceInformation.normalPrice
                        )}{" "}
                        TND
                      </p>

                      <span className="rounded-full bg-red-50 px-2 py-1 text-xs font-black text-red-600">
                        -
                        {
                          priceInformation.discountPercentage
                        }
                        %
                      </span>
                    </div>
                  )}

                  <p className="mt-1 text-3xl font-black text-green-600 sm:text-4xl">
                    {formatPrice(
                      priceInformation.finalPrice
                    )}

                    <span className="ms-1 text-base font-bold">
                      TND
                    </span>
                  </p>

                  {bien.type ===
                    "rent" && (
                    <p className="mt-1 text-sm font-medium text-slate-500">
                      /{" "}
                      {t(
                        "listing.month",
                        "mois"
                      )}
                    </p>
                  )}

                  {priceInformation.savedAmount >
                    0 && (
                    <p className="mt-2 text-xs font-bold text-red-500">
                      {t(
                        "listing.youSave",
                        "Économisez"
                      )}{" "}
                      {formatPrice(
                        priceInformation.savedAmount
                      )}{" "}
                      TND
                    </p>
                  )}
                </div>}
              </div>

              {/* Caractéristiques */}

              <div className="mt-7 grid grid-cols-2 gap-3 border-t border-slate-100 pt-6 sm:grid-cols-3 xl:grid-cols-5">

                {!isLand && (
                  <>
                    {isResidentialBien(bien.category) && (
                    <Feature
                      icon={LuBedDouble}
                      label={`${bien.chambres || 0} ${t(
                        Number(
                          bien.chambres
                        ) > 1
                          ? "listing.bedrooms"
                          : "listing.bedroom",
                        Number(
                          bien.chambres
                        ) > 1
                          ? "chambres"
                          : "chambre"
                      )}`}
                    />
                    )}

                    <Feature
                      icon={LuBath}
                      label={`${
                        bien.sallesBains ||
                        0
                      } ${t(
                        "listing.bathrooms",
                        "SDB"
                      )}`}
                    />

                    <Feature
                      icon={LuCar}
                      label={
                        Number(
                          bien.parking
                        )
                          ? t(
                              "listing.parking",
                              "Parking"
                            )
                          : t(
                              "listing.noParking",
                              "Sans parking"
                            )
                      }
                    />

                    {isResidentialBien(bien.category) && (
                    <Feature
                      icon={LuSofa}
                      label={
                        Number(
                          bien.meuble
                        )
                          ? t(
                              "listing.furnished",
                              "Meublé"
                            )
                          : t(
                              "listing.notFurnished",
                              "Non meublé"
                            )
                      }
                    />
                    )}
                  </>
                )}

                {Number(
                  bien.surface
                ) > 0 && (
                  <Feature
                    icon={LuMaximize2}
                    label={`${bien.surface} m²`}
                  />
                )}
              </div>
            </article>

            {/* =====================
                DESCRIPTION
            ====================== */}

            <article className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:p-7">
              <h2 className="text-xl font-black text-slate-900">
                {t(
                  "description",
                  "Description"
                )}
              </h2>

              <p className="mt-4 whitespace-pre-line text-sm leading-7 text-slate-600 sm:text-base">
                {bien.description ||
                  t(
                    "listing.noDescription",
                    "Aucune description disponible."
                  )}
              </p>
            </article>

            {/* =====================
                LOCALISATION
            ====================== */}

            {hasValidCoordinates && (
              <article className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm">

                <div className="flex flex-col gap-3 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-7">

                  <div>
                    <h2 className="text-xl font-black text-slate-900">
                      {t(
                        "listing.location",
                        "Localisation"
                      )}
                    </h2>

                    {locationText && (
                      <p className="mt-1 text-sm text-slate-500">
                        {locationText}
                      </p>
                    )}
                  </div>

                  <a
                    href={`https://www.google.com/maps?q=${latitude},${longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
                      inline-flex
                      items-center
                      gap-2
                      text-sm
                      font-bold
                      text-green-600
                      transition
                      hover:text-green-700
                    "
                  >
                    {t(
                      "listing.openMaps",
                      "Ouvrir dans Maps"
                    )}

                    <LuExternalLink
                      size={16}
                    />
                  </a>
                </div>

                <div className="h-[320px] w-full sm:h-[400px]">
                  <MapContainer
                    center={[
                      latitude,
                      longitude,
                    ]}
                    zoom={13}
                    scrollWheelZoom={
                      false
                    }
                    style={{
                      height: "100%",
                      width: "100%",
                    }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    <Marker
                      icon={listingMarkerIcon}
                      position={[
                        latitude,
                        longitude,
                      ]}
                    >
                      <Popup>
                        {bien.adresse ||
                          bien.libelle}
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </article>
            )}
          </div>

          {/* =====================
              CONTACT
          ====================== */}

          <aside className="lg:sticky lg:top-[98px]">
            <PublisherCard listing={bien} onMessage={()=>{setContactLandlord(true);setSendSuccess(false);setContactError("");}} />
            <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-xl shadow-slate-900/5 sm:p-6">

              <h2 className="text-xl font-black text-slate-900">
                {t(
                  "listing.contactLandlord",
                  "Contacter le propriétaire"
                )}
              </h2>

              <p className="mt-2 text-sm leading-6 text-slate-500">
                {t(
                  "listing.contactDescription",
                  "Obtenez plus d’informations ou organisez une visite de ce bien."
                )}
              </p>

              {/* Téléphone */}

              {bien.tel && (
                <a
                  href={`tel:${bien.tel}`}
                  className="
                    mt-6
                    flex
                    items-center
                    gap-3
                    rounded-2xl
                    border
                    border-green-100
                    bg-green-50
                    p-4
                    transition-all
                    hover:-translate-y-0.5
                    hover:border-green-200
                    hover:bg-green-100
                    hover:shadow-md
                  "
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-600 text-white shadow-sm">
                    <LuPhone
                      size={19}
                    />
                  </span>

                  <span className="min-w-0">
                    <span className="block text-xs font-semibold uppercase tracking-wide text-green-700">
                      {t(
                        "listing.phone",
                        "Téléphone"
                      )}
                    </span>

                    <span className="mt-1 block truncate text-base font-black text-slate-900">
                      <bdi dir="ltr">{bien.tel}</bdi>
                    </span>
                  </span>
                </a>
              )}

              {/* Succès */}

              {sendSuccess && (
                <div className="mt-5 flex items-start gap-3 rounded-2xl border border-green-200 bg-green-50 p-4 text-sm font-medium text-green-700">
                  <LuCheck
                    size={18}
                    className="mt-0.5 shrink-0"
                  />

                  {t(
                    "listing.messageSent",
                    "Votre message a bien été envoyé."
                  )}
                </div>
              )}

              {/* Erreur contact */}

              {contactError && (
                <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-600">
                  {contactError}
                </div>
              )}

              {/* Bouton contact */}

              {!isOwner &&
                !contactLandlord && (
                  <button
                    type="button"
                    onClick={() => {
                      setContactLandlord(
                        true
                      );

                      setSendSuccess(
                        false
                      );

                      setContactError(
                        ""
                      );
                    }}
                    className="
                      mt-6
                      inline-flex
                      h-12
                      w-full
                      items-center
                      justify-center
                      gap-2
                      rounded-2xl
                      bg-slate-950
                      px-6
                      text-sm
                      font-extrabold
                      text-white
                      shadow-lg
                      transition-all
                      hover:-translate-y-0.5
                      hover:bg-green-600
                      focus:outline-none
                      focus:ring-4
                      focus:ring-green-500/20
                    "
                  >
                    <LuMessageSquare
                      size={18}
                    />

                    {t(
                      "listing.contactLandlord",
                      "Contacter le propriétaire"
                    )}
                  </button>
                )}

              {/* Formulaire */}

              {contactLandlord &&
                !isOwner && (
                  <form
                    onSubmit={
                      handleFormSubmit
                    }
                    className="mt-6"
                  >
                    <div className="mb-5 flex items-center justify-between">

                      <h3 className="font-black text-slate-900">
                        {t(
                          "listing.sendMessage",
                          "Envoyer un message"
                        )}
                      </h3>

                      <button
                        type="button"
                        onClick={() => {
                          setContactLandlord(
                            false
                          );

                          setContactError(
                            ""
                          );
                        }}
                        aria-label={t(
                          "close",
                          "Fermer"
                        )}
                        className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                      >
                        <LuX
                          size={18}
                        />
                      </button>
                    </div>

                    <FormField
                      label={t(
                        "listing.name",
                        "Nom"
                      )}
                      id="contactName"
                    >
                      <input
                        id="contactName"
                        name="contactName"
                        type="text"
                        value={
                          formData.contactName
                        }
                        onChange={
                          handleFormChange
                        }
                        required
                        autoComplete="name"
                        placeholder={t(
                          "listing.namePlaceholder",
                          "Votre nom"
                        )}
                        className={
                          inputClassName
                        }
                      />
                    </FormField>

                    <FormField
                      label={t(
                        "listing.email",
                        "E-mail"
                      )}
                      id="email"
                    >
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={
                          formData.email
                        }
                        onChange={
                          handleFormChange
                        }
                        required
                        autoComplete="email"
                        placeholder="email@example.com"
                        className={
                          inputClassName
                        }
                      />
                    </FormField>

                    <FormField
                      label={t(
                        "listing.phone",
                        "Téléphone"
                      )}
                      id="phone"
                    >
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={
                          formData.phone
                        }
                        onChange={
                          handleFormChange
                        }
                        required
                        autoComplete="tel"
                        placeholder="+216 00 000 000"
                        className={
                          inputClassName
                        }
                      />
                    </FormField>

                    <FormField
                      label={t(
                        "listing.addMessage",
                        "Message"
                      )}
                      id="message"
                    >
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={
                          formData.message
                        }
                        onChange={
                          handleFormChange
                        }
                        required
                        className={`${inputClassName} min-h-[130px] resize-y py-3`}
                      />
                    </FormField>

                    <button
                      type="submit"
                      disabled={
                        sending
                      }
                      className="
                        mt-2
                        inline-flex
                        h-12
                        w-full
                        items-center
                        justify-center
                        gap-2
                        rounded-2xl
                        bg-green-600
                        px-6
                        text-sm
                        font-extrabold
                        text-white
                        shadow-lg
                        shadow-green-600/20
                        transition
                        hover:bg-green-700
                        disabled:cursor-not-allowed
                        disabled:bg-slate-300
                        disabled:shadow-none
                        focus:outline-none
                        focus:ring-4
                        focus:ring-green-500/20
                      "
                    >
                      {sending ? (
                        <>
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />

                          {t(
                            "sending",
                            "Envoi..."
                          )}
                        </>
                      ) : (
                        <>
                          <LuSend
                            size={17}
                          />

                          {t(
                            "listing.send",
                            "Envoyer le message"
                          )}
                        </>
                      )}
                    </button>
                  </form>
                )}

              {/* Propriétaire */}

              {isOwner && (
                <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                  {t(
                    "listing.ownerNotice",
                    "Vous êtes le propriétaire de cette annonce."
                  )}
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}

/* =====================================================
   GALERIE
===================================================== */

function ListingGallery({
  actions,
  images,
  photoPath,
  folder,
  title,

  galleryOpen,
  setGalleryOpen,

  activeImageIndex,
  setActiveImageIndex,

  shareLinkCopied,
  copiedLabel,
}) {
  useUiTranslation();
  /* Bloquer scroll + Escape */

  useEffect(() => {
    document.body.style.overflow =
      galleryOpen ? "hidden" : "";

    const handleKeyDown = (
      event
    ) => {
      if (
        event.key === "Escape" &&
        galleryOpen
      ) {
        setGalleryOpen(false);
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.body.style.overflow =
        "";

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [
    galleryOpen,
    setGalleryOpen,
  ]);

  /* URL sécurisée */

  const getImageUrl = (image) => photoUrl(image, folder);

  const handleImageError = (
    event
  ) => {
    event.currentTarget.onerror =
      null;

    event.currentTarget.src =
      FALLBACK_IMAGE;
  };

  const openGallery = (
    index = 0
  ) => {
    setActiveImageIndex(index);
    setGalleryOpen(true);
  };

  const photoActions = <div className={`listing-photo-actions absolute inset-x-0 top-3 z-20 mx-auto grid max-w-[1600px] pointer-events-none ${images.length ? "md:grid-cols-[2fr_1fr]" : ""}`}>
    <div dir="ltr" className="flex justify-end gap-2 px-3 pointer-events-none">
      {actions}
    </div>
  </div>;

  /* =========================
      AUCUNE IMAGE
  ========================== */

  if (!images.length) {
    return (
      <section className="relative flex h-40 items-center justify-center bg-slate-100 text-slate-500 sm:h-48">
        {photoActions}
        {shareLinkCopied && <span role="status" className="absolute bottom-2 text-sm">{copiedLabel}</span>}
        <div className="text-center">
          <LuImages
            size={38}
            className="mx-auto mb-3"
          />

          <p className="font-semibold"> {uiI18n.t("ui.no_photos_available")} </p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="relative bg-slate-950">
        {photoActions}

        {/* =====================
            MOBILE
        ====================== */}

        <div className="relative md:hidden">
          <Swiper
            slidesPerView={1}
            navigation={
              images.length > 1
            }
            pagination={{
              clickable: true,
            }}
            modules={[
              Navigation,
              Pagination,
            ]}
            onSlideChange={(
              swiper
            ) =>
              setActiveImageIndex(
                swiper.realIndex
              )
            }
          >
            {images.map(
              (image, index) => (
                <SwiperSlide
                  key={`${image}-${index}`}
                >
                  <button
                    type="button"
                    onClick={() =>
                      openGallery(
                        index
                      )
                    }
                    className="block h-[320px] w-full sm:h-[420px]"
                  >
                    <img
                      src={getImageUrl(
                        image
                      )}
                      alt={uiI18n.t("ui.listing_image", { title: title, number: index + 1 })}
                      loading={
                        index === 0
                          ? "eager"
                          : "lazy"
                      }
                      decoding="async"
                      onError={
                        handleImageError
                      }
                      className="h-full w-full object-cover"
                    />
                  </button>
                </SwiperSlide>
              )
            )}
          </Swiper>

          {/* compteur mobile */}

          <button
            type="button"
            onClick={() =>
              openGallery(
                activeImageIndex
              )
            }
            className="
              absolute
              bottom-4
              end-4
              z-20
              inline-flex
              items-center
              gap-2
              rounded-full
              bg-slate-950/75
              px-4
              py-2
              text-sm
              font-bold
              text-white
              backdrop-blur
            "
          >
            <LuImages
              size={17}
            />

            {activeImageIndex + 1}{" "}
            / {images.length}
          </button>
        </div>

        {/* =====================
            DESKTOP
        ====================== */}

        {/* Explicit row: a portrait photo must not grow the grid beyond its fixed height. */}
        <div
          className="
            mx-auto
            hidden
            max-w-[1600px]
            gap-2
            p-2
            md:grid
            md:h-[500px]
            md:grid-cols-[2fr_1fr]
            md:grid-rows-[minmax(0,1fr)]
            lg:h-[560px]
          "
        >

          {/* Image principale */}

          <button
            type="button"
            onClick={() =>
              openGallery(0)
            }
            className="
              group
              relative
              h-full
              overflow-hidden
              rounded-s-3xl
              bg-slate-900
            "
          >
            <img
              src={getImageUrl(
                images[0]
              )}
              alt={uiI18n.t("ui.main_image", { title: title })}
              loading="eager"
              decoding="async"
              onError={
                handleImageError
              }
              className="
                h-full
                w-full
                object-cover
                transition
                duration-500
                group-hover:scale-[1.02]
              "
            />

            <div className="absolute inset-0 bg-slate-950/0 transition group-hover:bg-slate-950/10" />
          </button>

          {/* Petites images */}

          <div className="grid h-full grid-cols-2 grid-rows-2 gap-2">
            {[1, 2, 3, 4].map(
              (imageIndex) => {
                const image =
                  images[
                    imageIndex
                  ];

                if (!image) {
                  return (
                    <div
                      key={
                        imageIndex
                      }
                      className="rounded-xl bg-slate-900"
                    />
                  );
                }

                const remainingImages =
                  images.length - 5;

                return (
                  <button
                    key={`${image}-${imageIndex}`}
                    type="button"
                    onClick={() =>
                      openGallery(
                        imageIndex
                      )
                    }
                    className={`
                      group
                      relative
                      overflow-hidden
                      bg-slate-900

                      ${
                        imageIndex ===
                        2
                          ? "rounded-tr-3xl"
                          : ""
                      }

                      ${
                        imageIndex ===
                        4
                          ? "rounded-br-3xl"
                          : ""
                      }
                    `}
                  >
                    <img
                      src={getImageUrl(
                        image
                      )}
                      alt={uiI18n.t("ui.listing_image", { title: title, number: imageIndex +
                        1 })}
                      loading="lazy"
                      decoding="async"
                      onError={
                        handleImageError
                      }
                      className="
                        h-full
                        w-full
                        object-cover
                        transition
                        duration-500
                        group-hover:scale-105
                      "
                    />

                    <div className="absolute inset-0 bg-slate-950/0 transition group-hover:bg-slate-950/15" />

                    {imageIndex ===
                      4 &&
                      remainingImages >
                        0 && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/60 text-white">
                          <LuImages
                            size={
                              28
                            }
                          />

                          <span className="mt-2 text-lg font-black">
                            +
                            {
                              remainingImages
                            }
                          </span>

                          <span className="text-xs font-semibold"> {uiI18n.t("ui.photos")} </span>
                        </div>
                      )}
                  </button>
                );
              }
            )}
          </div>
        </div>

        {/* Voir toutes les photos */}

        <button
          type="button"
          onClick={() =>
            openGallery(0)
          }
          className="
            absolute
            bottom-6
            end-6
            z-20
            hidden
            items-center
            gap-2
            rounded-2xl
            border
            border-white/30
            bg-white/95
            px-4
            py-3
            text-sm
            font-bold
            text-slate-900
            shadow-xl
            transition
            hover:-translate-y-0.5
            hover:bg-white
            md:inline-flex
          "
        >
          <LuImages size={18} /> {uiI18n.t("ui.view")} {images.length}{" "} {uiI18n.t("ui.photos")} </button>

        {/* Lien copié */}

        {shareLinkCopied && (
          <div
            className="
              absolute
              end-4
              top-20
              z-30
              flex
              items-center
              gap-2
              rounded-xl
              bg-slate-950
              px-4
              py-2
              text-sm
              font-semibold
              text-white
              shadow-2xl
              sm:end-6
              sm:top-24
            "
          >
            <LuCheck
              size={16}
              className="text-green-400"
            />

            {copiedLabel}
          </div>
        )}
      </section>

      {/* =====================
          GALERIE PLEIN ÉCRAN
      ====================== */}

      {galleryOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={uiI18n.t("ui.photo_gallery")}
          className="fixed inset-0 z-[100] bg-slate-950"
        >

          {/* barre supérieure */}

          <div
            className="
              absolute
              inset-x-0
              top-0
              z-30
              flex
              h-20
              items-center
              justify-between
              bg-gradient-to-b
              from-slate-950/90
              to-transparent
              px-4
              text-white
              sm:px-8
            "
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-bold backdrop-blur">
              <LuImages
                size={17}
              />

              {activeImageIndex +
                1}{" "}
              / {images.length}
            </div>

            <button
              type="button"
              onClick={() =>
                setGalleryOpen(
                  false
                )
              }
              aria-label={uiI18n.t("ui.close_gallery")}
              className="
                flex
                h-11
                w-11
                items-center
                justify-center
                rounded-full
                bg-white/10
                text-white
                transition
                hover:bg-white
                hover:text-slate-950
              "
            >
              <LuX size={24} />
            </button>
          </div>

          {/* Swiper plein écran */}

          <Swiper
            initialSlide={
              activeImageIndex
            }
            slidesPerView={1}
            navigation={
              images.length > 1
            }
            pagination={{
              clickable: true,
            }}
            modules={[
              Navigation,
              Pagination,
            ]}
            onSlideChange={(
              swiper
            ) =>
              setActiveImageIndex(
                swiper.realIndex
              )
            }
            className="h-full w-full"
          >
            {images.map(
              (image, index) => (
                <SwiperSlide
                  key={`fullscreen-${image}-${index}`}
                >
                  <div className="flex h-screen w-full items-center justify-center px-3 py-20 sm:px-12">
                    <img
                      src={getImageUrl(
                        image
                      )}
                      alt={uiI18n.t("ui.listing_image", { title: title, number: index + 1 })}
                      loading="lazy"
                      decoding="async"
                      onError={
                        handleImageError
                      }
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                </SwiperSlide>
              )
            )}
          </Swiper>
        </div>
      )}
    </>
  );
}

/* =====================================================
   FORM FIELD
===================================================== */

function FormField({
  label,
  id,
  children,
}) {
  useUiTranslation();
  return (
    <div className="mb-4">
      <label
        htmlFor={id}
        className="mb-2 block text-sm font-bold text-slate-700"
      >
        {label}
      </label>

      {children}
    </div>
  );
}

/* =====================================================
   FEATURE
===================================================== */

function Feature({
  icon: Icon,
  label,
}) {
  useUiTranslation();
  return (
    <div
      className="
        flex
        min-h-[64px]
        items-center
        gap-3
        rounded-2xl
        border
        border-transparent
        bg-slate-50
        p-3
        transition
        hover:border-green-100
        hover:bg-green-50/50
      "
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-green-600 shadow-sm">
        <Icon size={18} />
      </span>

      <span className="text-sm font-bold leading-5 text-slate-700">
        {label}
      </span>
    </div>
  );
}

export default Listing;
